import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ontruck/orc';
import Warning from 'assets/icons/Warning';
import { useTranslation } from 'react-i18next';
import { useOffers } from 'hooks/useOffers/useOffers';
import {
  putAcceptOffer,
  putRejectOffer,
  putBidOffer,
} from 'utils/network/http';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'utils/constants/routes';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import Notification from 'components/Notification';
import { NOTIFICATION_TYPES } from 'utils/constants/notifications';
import ButtonLoading from 'components/ButtonLoading';
import { useAuth } from 'hooks/useAuth/useAuth';
import BiddingField from 'components/BiddingField';
import { CURRENCIES, CURRENCIES_SYMBOLS } from 'utils/constants/formats';

const OfferActionsDetail = ({
  className,
  offerId,
  offerAmount,
  offerCurrency,
  biddableOffer,
  metaId,
  requested,
  refetchOffer,
  bidOffer,
  compliant,
  missing_documents,
  carrierUuid,
}) => {
  const { isAutoLogin, isDriverActive } = useAuth();
  const { t } = useTranslation('Offers');
  const {
    silentFetchOffers,
    startPollingOffers,
    cancelPollingOffers,
  } = useOffers();
  const [bidLoading, setBidLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showBiddingOption, setShowBiddingOption] = useState(false);
  const [biddingButtonEnabled, setBiddingButtonEnabled] = useState(true);
  const [bidAmount, setBidAmount] = useState(offerAmount);

  useEffect(() => {
    setBidAmount(offerAmount);
  }, [offerAmount]);

  const showAcceptOption = offerAmount > 0;
  let history = useHistory();
  const loading = acceptLoading && rejectLoading;

  const handleBidAmountChange = (value) => {
    if (isNaN(value) || value.trim().length === 0) {
      setBiddingButtonEnabled(false);
      value = '';
    } else {
      setBiddingButtonEnabled(true);
    }
    setBidAmount(value);
  };

  const redirectToOffers = useCallback(() => {
    history.push(ROUTES.OFFERS);
  }, [history]);

  const redirectToDocuments = useCallback(() => {
    carrierUuid &&
      history.push(
        ROUTES.DOCS_DOCUMENTS.DEFAULT(carrierUuid) +
          (missing_documents ? `?missing_documents=${missing_documents}` : '')
      );
  }, [history, carrierUuid, missing_documents]);

  const getCurrencySymbol = () => {
    switch (offerCurrency) {
      case CURRENCIES.EUR:
        return CURRENCIES_SYMBOLS.EUR;
      case CURRENCIES.GBP:
        return CURRENCIES_SYMBOLS.GBP;
      default:
        return CURRENCIES_SYMBOLS.EUR;
    }
  };

  const currentCurrency = getCurrencySymbol();

  const handleShowBiddingOption = () => {
    setShowBiddingOption(true);
  };

  const handleCancelBidding = () => {
    setShowBiddingOption(false);
  };

  const handleBidOffer = useCallback(
    async (e) => {
      e.stopPropagation();
      try {
        setBidLoading(true);
        setAcceptLoading(true);
        setError(false);
        cancelPollingOffers();
        if (bidAmount === offerAmount) {
          await putAcceptOffer(offerId);
          sendEventToSegment(TRACKING_EVENTS.OFFER_ACCEPT_FROM_DETAIL, {
            offerId,
            metaOfferId: metaId,
            isAutoLogin,
          });
        } else {
          await putBidOffer(offerId, bidAmount);
          sendEventToSegment(TRACKING_EVENTS.OFFER_BID_FROM_DETAIL, {
            offerId,
            metaOfferId: metaId,
            isAutoLogin,
            bidAmount,
          });
        }
        await refetchOffer();
      } catch (err) {
        toast(
          <Notification
            type={NOTIFICATION_TYPES.INFO}
            content={t('offerFailure')}
          />,
          {
            onClose: redirectToOffers,
          }
        );
        console.error(err);
        setError(true);
      } finally {
        setAcceptLoading(false);
        setBidLoading(false);
        silentFetchOffers();
        redirectToOffers();
      }
    },
    [
      metaId,
      offerId,
      bidAmount,
      offerAmount,
      redirectToOffers,
      t,
      cancelPollingOffers,
      silentFetchOffers,
      refetchOffer,
      isAutoLogin,
    ]
  );

  const handleAcceptOffer = useCallback(
    async (e) => {
      e.stopPropagation();
      try {
        setAcceptLoading(true);
        setBidLoading(true);
        setError(false);
        cancelPollingOffers();
        sendEventToSegment(TRACKING_EVENTS.OFFER_ACCEPT_FROM_DETAIL, {
          offerId,
          metaOfferId: metaId,
          isAutoLogin,
        });

        await putAcceptOffer(offerId);
        await refetchOffer();
      } catch (err) {
        toast(
          <Notification
            type={NOTIFICATION_TYPES.INFO}
            content={t('offerFailure')}
          />,
          {
            onClose: redirectToOffers,
          }
        );
        console.error(err);
        setError(true);
      } finally {
        setAcceptLoading(false);
        startPollingOffers();
      }
    },
    [
      offerId,
      redirectToOffers,
      t,
      cancelPollingOffers,
      startPollingOffers,
      metaId,
      refetchOffer,
      isAutoLogin,
    ]
  );

  const handleRejectOffer = useCallback(
    async (e) => {
      e.stopPropagation();
      try {
        setRejectLoading(true);
        setError(false);
        cancelPollingOffers();
        sendEventToSegment(TRACKING_EVENTS.OFFER_REJECT_FROM_DETAIL, {
          offerId,
          metaOfferId: metaId,
          isAutoLogin,
        });
        await putRejectOffer(offerId);
        await silentFetchOffers();
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setRejectLoading(false);
        startPollingOffers();
        redirectToOffers();
      }
    },
    [
      silentFetchOffers,
      offerId,
      redirectToOffers,
      cancelPollingOffers,
      startPollingOffers,
      metaId,
      isAutoLogin,
    ]
  );

  const controlsDisabled = bidOffer
    ? rejectLoading || !isDriverActive || bidOffer
    : rejectLoading || !isDriverActive;

  const renderBiddingActions = () => {
    return (
      <>
        {!loading && !error && requested && !bidOffer && (
          <div className="offer-actions__requested-container">
            <div className="offer-actions__requested-icon">
              <Icon glyph="time" size="1.6rem" />
            </div>
            <div className="offer-actions__requested-body">
              <div className="offer-actions__requested-title">
                <span>{t('offerRequested')}</span>
              </div>
              <div className="offer-actions__requested-subtitle">
                <span>{t('offerRequestedSubtitle')}</span>
              </div>
            </div>
          </div>
        )}
        {!loading && !error && !requested && bidOffer && (
          <div className="offer-actions__requested-container">
            <div className="offer-actions__requested-icon">
              <Icon glyph="coins" size="1.6rem" />
            </div>
            <div className="offer-actions__requested-body">
              <div className="offer-actions__requested-subtitle">
                <span>
                  {t('Offers:offerBidDetail', {
                    offerAmount: offerAmount,
                    currencySymbol: currentCurrency,
                  })}
                </span>
              </div>
            </div>
          </div>
        )}
        {!error &&
          !requested &&
          !bidOffer &&
          !showBiddingOption &&
          showAcceptOption && (
            <>
              <ButtonLoading
                testId="accept-offer-button"
                loading={acceptLoading}
                disabled={controlsDisabled}
                handleClick={handleAcceptOffer}
                extraClass="offer-actions__primary"
                text={t('acceptOfferToGivenPrice')}
              />
            </>
          )}
        {!error && !requested && !bidOffer && !showBiddingOption && (
          <>
            <ButtonLoading
              testId="show-bidding-button"
              loading={acceptLoading}
              disabled={controlsDisabled}
              handleClick={handleShowBiddingOption}
              extraClass="offer-actions__primary offer-actions__blue-background"
              text={t('requestOfferToAnotherPrice')}
              skin="blue"
            />
            <ButtonLoading
              testId="reject-offer-button"
              loading={rejectLoading}
              disabled={controlsDisabled}
              handleClick={handleRejectOffer}
              extraClass="offer-actions__secondary"
              text={t('rejectOffer')}
              ghost
            />
          </>
        )}
        {!error && !requested && !bidOffer && showBiddingOption && (
          <>
            <BiddingField
              testId="bidding-field"
              className="offer-actions__bidding"
              handleClick={handleBidOffer}
              value={bidAmount}
              disabledButton={bidAmount <= 0 || !biddingButtonEnabled}
              currencySymbol={currentCurrency}
              loading={bidLoading}
              disabled={controlsDisabled}
              onChange={handleBidAmountChange}
              onCancelClick={handleCancelBidding}
            />
            <div className="offer-actions__separator" />
            <ButtonLoading
              loading={rejectLoading}
              disabled={controlsDisabled}
              handleClick={handleRejectOffer}
              extraClass="offer-actions__secondary"
              text={t('rejectOffer')}
              ghost
            />
          </>
        )}
      </>
    );
  };

  const renderNonBiddingActions = () => {
    return (
      <>
        {!loading && !error && requested && (
          <div className="offer-actions__requested-container">
            <div className="offer-actions__requested-icon">
              <Icon glyph="time" size="1.6rem" />
            </div>
            <div className="offer-actions__requested-body">
              <div className="offer-actions__requested-title">
                <span>{t('offerRequested')}</span>
              </div>
              <div className="offer-actions__requested-subtitle">
                <span>{t('offerRequestedSubtitle')}</span>
              </div>
            </div>
          </div>
        )}
        {!error && !requested && showAcceptOption && (
          <>
            <ButtonLoading
              loading={acceptLoading}
              disabled={controlsDisabled}
              handleClick={handleAcceptOffer}
              extraClass="offer-actions__primary"
              text={t('acceptOffer')}
            />
          </>
        )}
        {!error && !requested && (
          <>
            <ButtonLoading
              loading={rejectLoading}
              disabled={controlsDisabled}
              handleClick={handleRejectOffer}
              extraClass="offer-actions__secondary"
              text={t('rejectOffer')}
              ghost
            />
          </>
        )}
      </>
    );
  };

  const renderNonCompliantActions = () => {
    return (
      <>
        <div className="offer-actions__non-compliant-container">
          <div className="offer-actions__non-compliant-icon">
            <Warning />
          </div>
          <div className="offer-actions__non-complian-body">
            <div className="offer-actions__non-compliant-title">
              <span>{t('offerNonCompliant')}</span>
            </div>
          </div>
        </div>
        <div className="offer-actions__non-compliant-action">
          <ButtonLoading
            loading={rejectLoading}
            disabled={controlsDisabled}
            handleClick={redirectToDocuments}
            extraClass="offer-actions__primary"
            text={t('updateDocs')}
          />
        </div>
      </>
    );
  };

  return (
    <div className={className} data-testid="offer-actions">
      {!compliant && (
        <div
          className="non-compliant-actions"
          data-testid="non-compliant-actions"
        >
          {renderNonCompliantActions()}
        </div>
      )}
      {compliant && (
        <div className="offer-actions">
          {biddableOffer ? renderBiddingActions() : renderNonBiddingActions()}
          {error && (
            <div className="offer-actions__error">
              <Icon glyph="close" size="2rem" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

OfferActionsDetail.defaultProps = {
  className: '',
  requested: false,
  biddableOffer: false,
  bidOffer: false,
  compliant: true,
};

OfferActionsDetail.propTypes = {
  className: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  metaId: PropTypes.number.isRequired,
  requested: PropTypes.bool,
  biddableOffer: PropTypes.bool,
  offerAmount: PropTypes.string,
  refetchOffer: PropTypes.func.isRequired,
  bidOffer: PropTypes.bool,
  compliant: PropTypes.bool,
};

export default OfferActionsDetail;
